import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { exactMoney } from '@/lib/format/money';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Un monto, en la moneda de quien lo mira.
 *
 * Delega en `lib/format/money`, que es donde vive la aritmética de toda la
 * app. Antes esto era un `Intl` propio con `CLP` **fijo**, así que un campo
 * personalizado de tipo moneda salía con `$` para un cliente que trabaja en
 * dólares o en UF — y con dos redondeos distintos según qué pantalla lo
 * dibujara.
 *
 * `currency` tiene default para no obligar a tocar cada llamador de golpe,
 * pero los tres que existen ya la pasan: el default es la red, no la norma.
 */
export const formatMoney = (v: number, currency: string = 'CLP') =>
  exactMoney(v, currency);
export const formatNumber = (v: number) => new Intl.NumberFormat('es-CL').format(v);
/** Lo que se muestra cuando no hay con qué medir. Mismo guion que las tarjetas del tablero. */
export const NO_DATA = '—';

/**
 * Un porcentaje, y «—» cuando no hay ninguno.
 *
 * `analytics.py` distingue **0 % de conversión** de **todavía no hay con qué
 * medir**: `_ratio()` devuelve `None` cuando el denominador es cero. Esta
 * función es la última pulgada de ese trabajo, y hasta hoy lo perdía —
 * `Intl.NumberFormat.format(null)` no falla, convierte `null` en `0` y escribe
 * «0 %». Una persona que no cerró nada porque no se le asignó nada aparecía
 * junto a otra que cerró 0 de 40, y en el tablero eso se lee como un equipo que
 * no vendió.
 *
 * `0` sigue siendo «0 %»: cero medido es un dato, y borrarlo sería el error
 * simétrico.
 */
export const formatPercent = (v: number | null | undefined, digits = 0) =>
  v === null || v === undefined || !Number.isFinite(v)
    ? NO_DATA
    : new Intl.NumberFormat('es-CL', {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(v);

/**
 * Ordenar por una métrica que puede no existir, sin romper la tabla.
 *
 * `a - b` con un `null` da `NaN`, y un comparador que devuelve `NaN` deja el
 * orden **indefinido**: la tabla se ve ordenada y no lo está. Es peor que el
 * «0 %» que arreglamos arriba, porque no se nota.
 *
 * «Sin dato» va siempre al final, en las dos direcciones. Tratarlo como cero lo
 * mandaría al fondo del ranking descendente —donde se lee como el peor— y a la
 * cabeza del ascendente, donde se lee como el mejor. No es ninguna de las dos:
 * es una fila sobre la que no hay nada que decir.
 */
export function byMetric(
  a: number | null | undefined,
  b: number | null | undefined,
  factor: number,
): number {
  const missingA = a === null || a === undefined || !Number.isFinite(a);
  const missingB = b === null || b === undefined || !Number.isFinite(b);
  if (missingA && missingB) return 0;
  if (missingA) return 1;
  if (missingB) return -1;
  return (a - b) * factor;
}

/** Initials for an avatar fallback: "María José Pérez" -> "MP". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Deterministic 0..n-1 bucket from a string, so the same contact always gets
 * the same avatar colour across sessions and machines.
 */
export function hashBucket(input: string, buckets: number): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % buckets;
}

/**
 * ¿Son el mismo correo?
 *
 * Un correo es el mismo escrito de las dos maneras: `Ana@Empresa.cl` y
 * `ana@empresa.cl` son una sola persona. Vive acá porque la comparación estaba
 * escrita tres veces —la ficha del ticket, el mock y el servidor— y el día que
 * «el mismo correo» signifique algo más (espacios, normalización Unicode) hay
 * que poder cambiarlo en un lugar y no ir a buscarlos.
 */
export const mismoCorreo = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
